/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect, useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import Stack from "../components/ui/Stack";
import Button from "../components/ui/Button";
import Paginator from "../components/ui/Paginator";
import Dimmed from "../components/ui/Dimmed";
import PluginCardSkeleton from "../components/skeleton/PluginCardSkeleton";
import EmptyView from "../components/EmptyView";
import PromotionItem from "../components/promotion/PromotionItem";
import SearchSortBar from "../components/booking/SearchSortBar";
import { StyledFooterStepButton } from "../components/FooterStepButton";
import { useGetPromotions } from "../lib/react-query/useGetPromotions";
import { useCart } from "../hooks/useCart";
import { useProductsSearchSort } from "../hooks/useProductsSearchSort";
import { FlowType, setFlow } from "../store/slices/flowSlice";
import { ROUTES } from "../constants/constants";
import Icon from "../components/ui/Icon";

const Grid = styled(Stack)`
  grid-template-columns: repeat(auto-fill, minmax(min(314px, 100%), 1fr));  
`;

const Promotions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { lines, setEntryRoute } = useCart();
  const [currentPage, setCurrentPage] = useState(1);
  const { searchValue, debouncedSearch, sortBy, orderField, orderDirection, setSearchValue, setSortBy } =
    useProductsSearchSort();

  const { data, isLoading, isFetching, isPlaceholderData } = useGetPromotions(
    { page: currentPage, search: debouncedSearch, orderField, orderDirection: orderDirection?.toLowerCase() },
    { placeholderData: keepPreviousData }
  );

  const promotions = data?.data || [];
  const totalPages = data?.metadata?.pages_count || 1;
  const hasCartLines = lines.length > 0;
  const isFiltering = !!debouncedSearch || !!sortBy;

  // Reset to the first page whenever the search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortBy]);

  useEffect(() => {
    setEntryRoute(location.pathname);
  }, [setEntryRoute, location.pathname]);

  useEffect(() => {
    dispatch(setFlow(FlowType.CART));
  }, [dispatch]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleCheckoutClick = () => {
    navigate(ROUTES.ORDER_SUMMARY);
  };

  return (
    <Container>
      <NavigationHeader title="Gift cards" navigation cart clientLogin />
      <Stack $gap="1rem">
        {!isLoading && (promotions.length > 0 || isFiltering) && (
          <SearchSortBar
            searchValue={searchValue}
            sortBy={sortBy}
            onSearchChange={setSearchValue}
            onSortChange={setSortBy}
          />
        )}

        {isLoading && (
          <Grid $gap="0.5rem">
            <PluginCardSkeleton withIncludes />
          </Grid>
        )}

        {!isLoading && promotions.length === 0 && <EmptyView icon={Icon.Gift} description="No gift cards available" />}

        {!isLoading && promotions.length > 0 && (
          <>
            <Dimmed $active={isPlaceholderData}>
              <Grid $gap="0.5rem">
                {promotions.map((p) => (
                  <PromotionItem key={p.id} promotion={p} />
                ))}
              </Grid>
            </Dimmed>
            {totalPages > 1 && (
              <Paginator
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isLoading={isFetching}
              />
            )}
          </>
        )}
      </Stack>

      {!isLoading && hasCartLines && (
        <StyledFooterStepButton>
          <Button title="Go to checkout" fullWidth onClick={handleCheckoutClick} />
        </StyledFooterStepButton>
      )}
    </Container>
  );
};

export default Promotions;
